import React from "react";
import PropTypes from "prop-types";
import { Pie } from "react-chartjs-2";
import formatNumber from "../../utils/formatNumber";
import "chartjs-plugin-datalabels";
import numbro from "numbro";

const tooltipWidth = 300;

export default class PieChart extends React.Component {
  constructor(props) {
    super(props);
    this.tooltipRef = React.createRef();
    this.onHover = this.onHover.bind(this);
    this.onLeave = this.onLeave.bind(this);
  }

  onHover(legendToolTips, legendTooltipPosition) {
    return (event, legendItem) => {
      let tooltip = this.tooltipRef;
      let { offsetX, offsetY } = event;
      if (tooltip && legendToolTips[legendItem.index].length > 20) {
        let offsetFromCursor = legendTooltipPosition === "left" ? -(tooltipWidth + 20) : 20;

        tooltip.current.innerHTML = legendToolTips[legendItem.index];
        tooltip.current.style.left = offsetX + offsetFromCursor + "px";
        tooltip.current.style.top = offsetY + "px";
        tooltip.current.style.padding = "6px";
      }
    };
  }

  onLeave() {
    let tooltip = this.tooltipRef;

    if (tooltip) {
      tooltip.current.style.padding = "0";
      tooltip.current.innerHTML = "";
    }
  }

  truncateLegendLabel(item) {
    let text = "";
    if (item.text && item.text.length > 20) {
      text = item.text.substring(0, 20) + "...";
    } else {
      text = item.text;
    }
    item.text = text;
    return !!item.text;
  }

  handleClick(event) {
    if (this.props.clickablePie) {
      let index = event[0]["_index"];
      window.open(this.props.data[index][2]);
    }
  }

  renderPlugins(data, allZerosOrEmpty) {
    let labels = { datalabels: { display: false } };

    if (allZerosOrEmpty) return labels;
    if (this.props.showRawLabels) return { datalabels: { display: true } };

    if (this.props.showPercent) {
      labels = {
        datalabels: {
          formatter: (value) => {
            let sum = 0;
            data.map((data) => (sum += data));
            let percentage = ((value * 100) / sum).toFixed(2);
            return percentage > 10 ? percentage + "%" : "";
          },
          color: "#43474e",
          fontWeight: "800"
        }
      };
    }

    return labels;
  }

  render() {
    const {
      data: chartValues,
      currency = "",
      addOtherLabel,
      isSortable,
      maxSlices,
      customTooltip,
      responsive,
      width,
      height,
      showLegend,
      legendPosition,
      isLegendTooltip,
      isClubPilates,
      backgroundColorClubPilates,
      backgroundColor,
      hoverBackgroundColorClubPilates,
      hoverBackgroundColor,
      legendTooltipPosition = "right"
    } = this.props;
    const labels = [];
    let data = [];
    const indexForOther = maxSlices - 1;
    let allZerosOrEmpty = true;

    const showLegendLabelTooltip =
      showLegend && isLegendTooltip && (legendPosition === "right" || legendPosition === "left");

    if (isSortable) {
      chartValues.sort((a, b) => {
        let num1 = numbro.unformat(a[1]);
        let num2 = numbro.unformat(b[1]);
        // by desc
        return num2 - num1;
      });
    }

    const populateLabelsAndDataArrays = (label, num) => {
      labels.push(label);
      data.push(num);
    };

    const handleOtherLabelAndItsData = (num, index) => {
      data[indexForOther] += num;
      chartValues.length - 1 === index ? (labels[indexForOther] = "Other") : null;
    };

    Array.isArray(chartValues) &&
      chartValues.forEach((subArray, index) => {
        //converts string to number
        let num = numbro.unformat(subArray[1]);
        allZerosOrEmpty = allZerosOrEmpty && num === 0;
        let label = subArray[0];

        if (addOtherLabel) {
          index <= indexForOther
            ? populateLabelsAndDataArrays(label, num)
            : handleOtherLabelAndItsData(num, index);
        } else populateLabelsAndDataArrays(label, num);
      });

    if (allZerosOrEmpty) {
      data = Array.from(
        {
          length:
            chartValues.length && chartValues.length < maxSlices ? chartValues.length : maxSlices
        },
        () => 1
      );
    }

    if (chartValues) {
      return (
        <>
          <Pie
            width={width}
            height={height}
            getElementsAtEvent={(e) => this.handleClick(e)}
            data={{
              labels: labels,
              datasets: [
                {
                  data: data,
                  backgroundColor: isClubPilates ? backgroundColorClubPilates : backgroundColor,
                  hoverBackgroundColor: isClubPilates
                    ? hoverBackgroundColorClubPilates
                    : hoverBackgroundColor,
                  borderWidth: 1,
                  borderColor: "#5f6566"
                }
              ]
            }}
            options={{
              maintainAspectRatio: false,
              responsive,
              plugins: this.renderPlugins(data, allZerosOrEmpty),
              tooltips: {
                callbacks: {
                  label: function (tooltipItem, data) {
                    const dataset = data.datasets[tooltipItem.datasetIndex];
                    const currentValue = dataset.data[tooltipItem.index];
                    return allZerosOrEmpty
                      ? ""
                      : !customTooltip
                      ? `${currency}${formatNumber(currentValue, "0,0")}`
                      : chartValues[tooltipItem.index][2];
                  },
                  title: function (tooltipItem, data) {
                    return allZerosOrEmpty ? "" : data.labels[tooltipItem[0].index];
                  }
                }
              },

              legend: {
                display: showLegend,
                position: legendPosition || "bottom",
                onHover: showLegendLabelTooltip && this.onHover(labels, legendTooltipPosition),
                onLeave: showLegendLabelTooltip && this.onLeave,
                labels: {
                  fontColor: "#87919f",
                  fontStyle: "bold",
                  filter: showLegendLabelTooltip && this.truncateLegendLabel
                }
              }
            }}
          />
          <div id="tooltip" style={legendTooltipStyle} ref={this.tooltipRef} />
        </>
      );
    }
  }
}

const legendTooltipStyle = {
  width: tooltipWidth + "px",
  textAlign: "center",
  backgroundColor: "#000",
  color: "#fff",
  position: "absolute",
  zIndex: "1",
  borderRadius: "6px",
  fontSize: "13px"
};

PieChart.propTypes = {
  data: PropTypes.array.isRequired,
  showLegend: PropTypes.bool,
  legendPosition: PropTypes.string,
  clickablePie: PropTypes.bool,
  backgroundColor: PropTypes.array,
  hoverBackgroundColor: PropTypes.array,
  isClubPilates: PropTypes.bool,
  backgroundColorClubPilates: PropTypes.array,
  hoverBackgroundColorClubPilates: PropTypes.array,
  showPercent: PropTypes.bool,
  currency: PropTypes.string,
  addOtherLabel: PropTypes.bool,
  maxSlices: PropTypes.number,
  isSortable: PropTypes.bool,
  customTooltip: PropTypes.bool,
  responsive: PropTypes.bool,
  isLegendTooltip: PropTypes.bool,
  width: PropTypes.number,
  height: PropTypes.number
};

PieChart.defaultProps = {
  data: [],
  isClubPilates: false,
  addOtherLabel: true,
  backgroundColor: [
    "#ff664e",
    "#1794ca",
    "#21e981",
    "#df30e5",
    "#1cdac4",
    "#7d4eff",
    "#ffbe4e",
    "#ca5317",
    "red"
  ],
  hoverBackgroundColor: [
    "#ed5740",
    "#0084b8",
    "#00d872",
    "#cc0ad3",
    "#00c8b2",
    "#6a3eed",
    "#efae3e",
    "#b74305",
    "red"
  ],
  backgroundColorClubPilates: [
    "#5c8cc9",
    "#87bec3",
    "#54a5ac",
    "#85959f",
    "#dadfe2",
    "#a7c0e2",
    "#b3d7db",
    "#5b676e",
    ""
  ],
  hoverBackgroundColorClubPilates: [],
  maxSlices: 8,
  isSortable: true,
  customTooltip: false,
  responsive: true
};
